import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Calendar, MapPin, CheckCircle2 } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [rsvps, setRsvps] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    api.get("/events/my-rsvps").then(({ data }) => setRsvps(data.events || [])).catch(() => {});
    api.get("/events").then(({ data }) => setUpcoming(data.slice(0, 3)));
  }, []);

  const membershipLabel = {
    pending: "Pending",
    active: "Active",
  }[user?.membership_status] || "Pending";

  return (
    <section className="py-16 bg-pearl min-h-[calc(100vh-5rem)]" data-testid="dashboard-page">
      <div className="section-container">
        <p className="eyebrow">Members' area</p>
        <h1 className="mt-3 font-display text-5xl text-navy">Namo Buddhāya, {user?.name?.split(" ")[0]}.</h1>
        <p className="mt-3 text-slate-600">A short overview of your membership and upcoming gatherings.</p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-navy/10 p-8 rounded-sm">
            <p className="eyebrow">Membership</p>
            <div className="mt-3 font-display text-3xl text-navy">
              {user?.membership_tier ? user.membership_tier.charAt(0).toUpperCase() + user.membership_tier.slice(1) : "—"}
            </div>
            <div className={`mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-2.5 py-1 rounded-sm ${
              user?.membership_status === "active" ? "bg-saffron/15 text-saffron" : "bg-navy/10 text-navy/70"
            }`}>
              <CheckCircle2 size={12} /> {membershipLabel}
            </div>
            {user?.membership_status !== "active" && (
              <Link to="/membership" data-testid="dash-activate-btn" className="mt-5 inline-block text-sm underline underline-offset-2 text-navy hover:text-saffron">
                Activate your membership →
              </Link>
            )}
          </div>

          <div className="bg-white border border-navy/10 p-8 rounded-sm">
            <p className="eyebrow">Events attending</p>
            <div className="mt-3 font-display text-5xl text-gold">{rsvps.length}</div>
            <p className="mt-2 text-sm text-slate-500">Upcoming RSVPs in your calendar.</p>
          </div>

          <div className="bg-white border border-navy/10 p-8 rounded-sm">
            <p className="eyebrow">Community</p>
            <div className="mt-3 font-display text-3xl text-navy">ABGN-UK Sangha</div>
            <p className="mt-2 text-sm text-slate-500">Reach out anytime at info@ambedkaritebuddhistglobalnetworkuk.com.</p>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="font-display text-3xl text-navy">Your upcoming events</h2>
            <div className="mt-6 space-y-4">
              {rsvps.length === 0 ? (
                <p className="text-slate-500">You haven't RSVP'd to any event yet. <Link className="text-navy underline" to="/events">Browse events →</Link></p>
              ) : (
                rsvps.map((e) => <EventRow key={e.event_id} e={e} />)
              )}
            </div>
          </div>
          <div>
            <h2 className="font-display text-3xl text-navy">Community gatherings</h2>
            <div className="mt-6 space-y-4">
              {upcoming.length === 0 ? (
                <p className="text-slate-500">No public events yet.</p>
              ) : (
                upcoming.map((e) => <EventRow key={e.event_id} e={e} />)
              )}
              <Link to="/events" data-testid="dash-events-link" className="inline-block mt-4 text-sm text-navy underline underline-offset-2 hover:text-saffron">
                See all events →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function EventRow({ e }) {
  const d = new Date(e.event_date);
  return (
    <div className="bg-white border border-navy/10 p-5 rounded-sm flex items-center gap-4">
      <div className="text-center font-display leading-none min-w-14">
        <div className="text-[11px] uppercase tracking-widest text-saffron">{d.toLocaleDateString("en-GB", { month: "short" })}</div>
        <div className="text-3xl text-navy">{d.getDate()}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-lg text-navy truncate">{e.title}</div>
        <div className="text-xs text-slate-500 flex items-center gap-2 mt-1">
          <MapPin size={12} /> {e.location} · <Calendar size={12} /> {d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}
